import type { ApiComponents } from '@aztec-realms/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { browserApiClient, requiredApiData } from './browser-api';
import { inboxQueryKey } from './earned-inbox-api';
import { tribeSocialQueryKey } from './tribe-query-keys';

export type TribeSocialBoard = ApiComponents['schemas']['TribeSocialBoardResponse'];
export type CelebrationTarget = TribeSocialBoard['celebration_targets'][number];
export type CelebrationExpression = TribeSocialBoard['celebrations'][number]['expression'];
export type SocialCommand =
  | { type: 'trade-thanks'; commandId: string; tradeId: string }
  | {
      type: 'celebrate';
      commandId: string;
      targetKind: CelebrationTarget['target_kind'];
      targetId: string;
      phase: CelebrationTarget['phase'];
      expression: CelebrationExpression;
    }
  | {
      type: 'report-celebration';
      commandId: string;
      celebrationId: string;
      reason: ApiComponents['schemas']['AssistanceReportRequest']['reason'];
    };

export async function loadTribeSocial(tribeId: string, signal?: AbortSignal) {
  const response = await browserApiClient().GET('/api/v1/tribes/{tribe_id}/social', {
    params: { path: { tribe_id: tribeId } },
    signal,
  });
  return requiredApiData(
    response.data,
    response.error,
    'Your Tribe’s thanks and celebrations could not be loaded.',
  );
}

export async function submitSocialCommand(tribeId: string, command: SocialCommand) {
  const client = browserApiClient({ mutation: true });
  const header = { 'Idempotency-Key': command.commandId };
  const path = { tribe_id: tribeId };
  const response =
    command.type === 'trade-thanks'
      ? await client.POST('/api/v1/tribes/{tribe_id}/social/trades/{trade_id}/thanks', {
          params: { path: { ...path, trade_id: command.tradeId }, header },
          body: {},
        })
      : command.type === 'celebrate'
        ? await client.POST('/api/v1/tribes/{tribe_id}/social/celebrations', {
            params: { path, header },
            body: {
              target_kind: command.targetKind,
              target_id: command.targetId,
              phase: command.phase,
              expression: command.expression,
            },
          })
        : await client.POST(
            '/api/v1/tribes/{tribe_id}/social/celebrations/{celebration_id}/report',
            {
              params: { path: { ...path, celebration_id: command.celebrationId }, header },
              body: { reason: command.reason },
            },
          );
  return requiredApiData(
    response.data,
    response.error,
    'Your social action could not be confirmed.',
  );
}

export function useTribeSocial(tribeId: string, enabled: boolean) {
  return useQuery({
    queryKey: tribeSocialQueryKey(tribeId),
    queryFn: ({ signal }) => loadTribeSocial(tribeId, signal),
    enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

export function useSocialCommand(tribeId: string) {
  const client = useQueryClient();
  return useMutation({
    networkMode: 'always',
    retry: false,
    mutationFn: (command: SocialCommand) => {
      if (typeof navigator !== 'undefined' && !navigator.onLine)
        throw new Error('Reconnect before thanking or celebrating with your Tribe.');
      return submitSocialCommand(tribeId, command);
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: inboxQueryKey });
      await client.invalidateQueries({ queryKey: tribeSocialQueryKey(tribeId), exact: true });
    },
  });
}
